import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import * as api from "../api";
import TRANSLATIONS from "../translations";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Chip, SectionHeader, Input, Textarea, Callout, Spinner } from "./UI";
import { DraggableMarker } from "./Map";

function Order({ setView, setLastOrder }) {
  const t = TRANSLATIONS.fr;
  const [step, setStep]       = useState(1);
  const [client, setClient]   = useState("");
  const [phone, setPhone]     = useState("");
  const [address, setAddress] = useState("");
  const [date, setDate]       = useState("");
  const [time, setTime]       = useState("");
  const [notes, setNotes]     = useState("");
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [error, setError]     = useState("");
  const [clientInfo, setClientInfo] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [allSlots, setAllSlots] = useState(null);
  const [coords, setCoords] = useState(null); // { lat, lng }
  const [promoCode, setPromoCode] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // { valid, discount, message }

  const today = new Date().toISOString().split("T")[0];

  // Load all slots info on mount
  useEffect(() => {
    api.getCollectionSlots().then(data => setAllSlots(data)).catch(() => {});
  }, []);

  // Load available slots when date changes
  useEffect(() => {
    if (!date) { setAvailableSlots([]); return; }
    setSlotsLoading(true);
    api.getAvailableSlots(date).then(data => {
      setAvailableSlots(data.slots || []);
      setTime(""); // Reset time selection
    }).catch(() => setAvailableSlots([])).finally(() => setSlotsLoading(false));
  }, [date]);

  // Client auto-lookup (public — no auth needed)
  const [clientLookedUp, setClientLookedUp] = useState(false);
  async function lookupClient() {
    const p = phone.replace(/\s/g, "");
    if (p.length >= 9) {
      try {
        const info = await api.getClientInfo(p);
        if (info) {
          setClientLookedUp(true);
          if (!client && info.name) setClient(info.name);
          if (!address && info.address) setAddress(info.address);
          // Also try to get order count for loyalty badge
          try {
            const data = await api.getClientOrders(p);
            if (data && data.orders) {
              setClientInfo({ totalOrders: data.orders.length, client: info.name, address: info.address });
            }
          } catch { setClientInfo({ totalOrders: 0 }); }
        }
      } catch { setClientLookedUp(false); /* new client */ }
    }
  }

  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapPickerLat, setMapPickerLat] = useState(null);
  const [mapPickerLng, setMapPickerLng] = useState(null);

  function reverseGeocode(lat, lng) {
    return fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=18&addressdetails=1`, { headers: { "User-Agent": "SetalExpress/1.0" } })
      .then(r => r.json())
      .then(d => {
        const a = d.address || {};
        const parts = [a.house_number, a.road, a.neighbourhood, a.suburb, a.city || a.town || a.village].filter(Boolean);
        return parts.length >= 2 ? parts.join(", ") : d.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      })
      .catch(() => `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
  }

  function getGPS() {
    if (!navigator.geolocation) {
      setError("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }
    setGpsLoading(true); setError("");
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setMapPickerLat(lat);
        setMapPickerLng(lng);
        const addr = await reverseGeocode(lat, lng);
        setAddress(addr);
        setShowMapPicker(true);
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === 1) setError("Accès GPS refusé. Autorisez la localisation dans les paramètres de votre navigateur.");
        else if (err.code === 2) setError("Position GPS non disponible. Vérifiez que le GPS est activé sur votre appareil.");
        else if (err.code === 3) setError("Délai GPS dépassé. Réessayez dans un endroit avec meilleure réception.");
        else setError("Impossible d'obtenir la position GPS");
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }

  async function handleSubmit() {
    setLoading(true); setError("");
    // Validations
    const cleanPhone = phone.replace(/\s/g, "");
    if (cleanPhone.length < 9 || cleanPhone.length > 10) {
      setError("Le numéro de téléphone doit contenir 9 ou 10 chiffres"); setLoading(false); return;
    }
    if (date < today) {
      setError("La date de collecte ne peut pas être dans le passé"); setLoading(false); return;
    }
    try {
      const orderData = {
        client, phone, address, date, time, notes,
        latitude: mapPickerLat || coords?.lat || null,
        longitude: mapPickerLng || coords?.lng || null,
      };
      if (promoCode.trim()) orderData.promo_code = promoCode.trim();
      const order = await api.createOrder(orderData);
      setLastOrder(order);
      setView("confirm");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const stepLabels = [t.step1, t.step2, t.step3];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, paddingBottom: 78 }}>
      {/* ─── En-tête ─────────────────────────────────────────────────────── */}
      <div style={{ background: C.navy, color: C.inkOn, padding: "16px 20px 22px" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <button onClick={() => setView("landing")} className="btn-bounce" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,255,255,.10)", border: "1px solid rgba(255,255,255,.18)",
            color: C.inkOn, borderRadius: R.md, padding: "5px 11px",
            fontSize: F.size.base, fontWeight: F.weight.medium, marginBottom: 12,
          }}>
            <Icon name="back" size={14} />
            {t.back}
          </button>
          <h1 style={{ fontSize: F.size["2xl"], fontWeight: F.weight.semi, letterSpacing: "-.02em" }}>
            {t.orderTitle}
          </h1>
        </div>
      </div>

      {/* ─── Fil d'étapes ────────────────────────────────────────────────── */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <ol style={{
          maxWidth: 560, margin: "0 auto", padding: "12px 20px",
          display: "flex", alignItems: "center", listStyle: "none",
        }}>
          {[1, 2, 3].map(s => {
            const isCompleted = s < step;
            const isActive = s === step;
            return (
              <React.Fragment key={s}>
                <li
                  aria-current={isActive ? "step" : undefined}
                  style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
                >
                  <span className="stepper-icon" style={{
                    width: 26, height: 26, borderRadius: R.pill, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: F.size.sm, fontWeight: F.weight.semi,
                    background: isActive ? C.navy : isCompleted ? C.navySoft : C.surfaceAlt,
                    color: isActive ? C.inkOn : isCompleted ? C.navy : C.inkFaint,
                    border: `1px solid ${isActive ? C.navy : C.border}`,
                    transition: "background .2s ease, color .2s ease",
                    ...numeric,
                  }}>
                    {isCompleted ? <Icon name="check" size={14} strokeWidth={2} /> : s}
                  </span>
                  <span className="stepper-label" style={{
                    fontSize: F.size.base,
                    fontWeight: isActive ? F.weight.semi : F.weight.medium,
                    color: isActive ? C.navy : isCompleted ? C.inkBody : C.inkFaint,
                    whiteSpace: "nowrap",
                  }}>
                    {stepLabels[s - 1]}
                  </span>
                </li>
                {s < 3 && (
                  <li aria-hidden="true" style={{
                    flex: 1, minWidth: 14, height: 1, margin: "0 10px",
                    background: s < step ? C.navy : C.border,
                    transition: "background .3s ease",
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </ol>
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: 20 }}>
        <Card className="animate-fadeInUp">
          {/* ─── Étape 1 : coordonnées ─────────────────────────────────── */}
          {step === 1 && (
            <div>
              <SectionHeader title={t.yourCoords} icon="user" />
              <Input
                label={t.phoneLabel}
                value={phone}
                onChange={v => { if (v.replace(/\s/g, "").length <= 10) { setPhone(v); setClientLookedUp(false); } }}
                type="tel"
                icon="mobile"
                placeholder="ex : 77 123 45 67"
                required
                onBlur={lookupClient}
                maxLength={13}
                style={{ marginBottom: phone && phone.replace(/\s/g, "").length < 9 ? 6 : 14 }}
              />
              {phone && phone.replace(/\s/g, "").length < 9 && (
                <p style={{
                  display: "flex", alignItems: "center", gap: 5, marginBottom: 14,
                  fontSize: F.size.sm, color: C.warning,
                }}>
                  <Icon name="alert" size={12} />
                  Le numéro doit contenir au moins 9 chiffres
                </p>
              )}

              {clientLookedUp && (
                <Callout tone="success" icon="userCheck" title={`Bienvenue ${client} !`} style={{ marginBottom: 14 }}>
                  Vos informations ont été pré-remplies
                  {clientInfo && clientInfo.totalOrders > 0 && (
                    <span style={numeric}> · {clientInfo.totalOrders} commande(s) passée(s)</span>
                  )}
                </Callout>
              )}

              <Input label={t.nameLabel} value={client} onChange={setClient} icon="user" placeholder="ex : Moussa Diallo" required />

              <Btn fullWidth iconRight="forward" onClick={() => {
                const cp = phone.replace(/\s/g, "");
                if (!client || !phone) { setError(t.fillFields); }
                else if (cp.length < 9 || cp.length > 10) { setError("Le numéro doit contenir 9 ou 10 chiffres"); }
                else { setError(""); setStep(2); }
              }}>
                {t.nextBtn}
              </Btn>

              {error && <Callout tone="danger" style={{ marginTop: 12 }}>{error}</Callout>}
            </div>
          )}

          {/* ─── Étape 2 : détails de la collecte ──────────────────────── */}
          {step === 2 && (
            <div>
              <SectionHeader title={t.collectDetails} icon="location" />

              <div style={{ marginBottom: 14 }}>
                <label htmlFor="order-address" style={{
                  display: "block", marginBottom: 5,
                  fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody,
                }}>
                  {t.addressLabel} <span style={{ color: C.danger }} aria-hidden="true">*</span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="order-address"
                    className="field"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Votre adresse de collecte"
                    required
                    style={{ flex: 1 }}
                  />
                  <Btn
                    variant="white"
                    icon="location"
                    onClick={getGPS}
                    disabled={gpsLoading}
                    loading={gpsLoading}
                    title="Utiliser ma position GPS"
                  >
                    GPS
                  </Btn>
                </div>

                {/* Carte interactive après GPS */}
                {showMapPicker && mapPickerLat && mapPickerLng && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ borderRadius: R.md, overflow: "hidden", border: `1px solid ${C.border}`, height: 220 }}>
                      <MapContainer
                        center={[mapPickerLat, mapPickerLng]}
                        zoom={17}
                        style={{ height: "100%", width: "100%" }}
                        key={`order-map-${mapPickerLat.toFixed(4)}-${mapPickerLng.toFixed(4)}`}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
                        <DraggableMarker lat={mapPickerLat} lng={mapPickerLng} onMove={async (lat, lng) => {
                          setMapPickerLat(lat);
                          setMapPickerLng(lng);
                          setCoords({ lat, lng });
                          const addr = await reverseGeocode(lat, lng);
                          setAddress(addr);
                        }} />
                      </MapContainer>
                    </div>
                    <p style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      fontSize: F.size.sm, color: C.inkMuted, margin: "7px 0 0",
                    }}>
                      <Icon name="hand" size={13} />
                      Déplacez le marqueur pour ajuster votre position exacte
                    </p>
                    {coords && (
                      <div style={{ marginTop: 7 }}>
                        <Chip tone="success" icon="target">
                          <span style={numeric}>GPS : {mapPickerLat.toFixed(6)}, {mapPickerLng.toFixed(6)}</span>
                        </Chip>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ─── Collecte : quand nous venons CHERCHER le linge sale ───── */}
              <div style={{
                border: `1px solid ${C.border}`, borderRadius: R.lg,
                padding: 14, marginBottom: 14, background: C.surfaceAlt,
              }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 12 }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: R.md, flexShrink: 0,
                    background: C.navy, color: C.inkOn,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon name="collect" size={15} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md }}>
                      Collecte de votre linge
                    </div>
                    <div style={{ fontSize: F.size.base, color: C.inkMuted, lineHeight: 1.5 }}>
                      Choisissez quand nous venons <strong>chercher votre linge sale</strong> chez vous.
                    </div>
                  </div>
                </div>

                <Input
                  label="Date de collecte"
                  value={date}
                  onChange={setDate}
                  type="date"
                  min={today}
                  required
                  style={{ marginBottom: date ? 12 : 0 }}
                />

                {date && (
                  <div>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 5, marginBottom: 7,
                      fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody,
                    }}>
                      Heure de passage
                      <span style={{ color: C.danger }} aria-hidden="true">*</span>
                    </div>

                    {slotsLoading ? (
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "12px 2px", fontSize: F.size.base, color: C.inkMuted,
                      }}>
                        <Spinner inline /> Recherche des créneaux…
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <Callout tone="warning">
                        Nous ne passons pas ce jour-là. Choisissez une autre date de collecte.
                      </Callout>
                    ) : (
                      <div
                        role="radiogroup"
                        aria-label="Heure de passage pour la collecte"
                        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))", gap: 8 }}
                      >
                        {availableSlots.map(slot => {
                          const isSelected = time === slot.label;
                          return (
                            <button
                              key={slot.id}
                              type="button"
                              role="radio"
                              aria-checked={isSelected}
                              className="btn-bounce"
                              onClick={() => setTime(slot.label)}
                              style={{
                                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                minHeight: 44, padding: "8px 10px", borderRadius: R.md,
                                border: `1px solid ${isSelected ? C.navy : C.border}`,
                                background: isSelected ? C.navy : C.surface,
                                color: isSelected ? C.inkOn : C.ink,
                                fontSize: F.size.md,
                                fontWeight: isSelected ? F.weight.semi : F.weight.medium,
                                ...numeric,
                              }}
                            >
                              {isSelected && <Icon name="check" size={14} strokeWidth={2.2} />}
                              {slot.start_time} – {slot.end_time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Textarea label={t.notesLabel} value={notes} onChange={setNotes} placeholder="Instructions particulières…" />

              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="white" icon="back" onClick={() => setStep(1)}>{t.prevBtn}</Btn>
                <Btn style={{ flex: 1 }} iconRight="forward" onClick={() => {
                  if (address && date && time) { setError(""); setStep(3); }
                  else setError(t.fillFields);
                }}>{t.nextBtn}</Btn>
              </div>

              {error && <Callout tone="danger" style={{ marginTop: 12 }}>{error}</Callout>}
            </div>
          )}

          {/* ─── Étape 3 : récapitulatif ───────────────────────────────── */}
          {step === 3 && (
            <div>
              <SectionHeader title={t.summary} icon="list" />

              <div style={{
                background: C.surfaceAlt, border: `1px solid ${C.border}`,
                borderRadius: R.md, padding: 14, marginBottom: 16,
              }}>
                {[
                  ["user", t.client, client],
                  ["phone", t.telephone, phone],
                  ["location", "Adresse", address],
                  ["calendar", t.date, date],
                  ["clock", "Heure", time],
                  ...(notes ? [["edit", "Notes", notes]] : []),
                ].map(([iconName, k, v]) => (
                  <div key={k} style={{
                    display: "flex", gap: 10, alignItems: "flex-start",
                    fontSize: F.size.base, marginBottom: 9,
                  }}>
                    <span style={{ color: C.inkFaint, marginTop: 2 }}><Icon name={iconName} size={14} /></span>
                    <span style={{ fontWeight: F.weight.medium, color: C.inkBody, minWidth: 82 }}>{k}</span>
                    <span style={{ color: C.ink, flex: 1, minWidth: 0, ...numeric }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Code promo */}
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="order-promo" style={{
                  display: "flex", alignItems: "center", gap: 6, marginBottom: 5,
                  fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody,
                }}>
                  <Icon name="ticket" size={14} color={C.inkMuted} />
                  Code promo
                  <span style={{ color: C.inkFaint, fontWeight: F.weight.normal }}>· optionnel</span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="order-promo"
                    className="field mono"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                    placeholder="EX : PROMO20"
                    style={{ flex: 1, textTransform: "uppercase" }}
                  />
                  <Btn variant="white" disabled={!promoCode.trim() || promoValidating} loading={promoValidating} onClick={async () => {
                    setPromoValidating(true);
                    try {
                      const r = await api.validatePromo(promoCode.trim());
                      setPromoResult({ valid: r.valid, discount: r.discount_preview, message: r.valid ? `-${r.type === "percentage" ? r.value + "%" : parseInt(r.value).toLocaleString() + " FCFA"}` : r.error });
                    } catch (err) { setPromoResult({ valid: false, message: err.message }); }
                    finally { setPromoValidating(false); }
                  }}>
                    {promoValidating ? "…" : "Vérifier"}
                  </Btn>
                </div>
                {promoResult && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6, marginTop: 7,
                    fontSize: F.size.base, fontWeight: F.weight.medium,
                    color: promoResult.valid ? C.success : C.danger,
                  }}>
                    <Icon name={promoResult.valid ? "success" : "error"} size={14} />
                    <span style={numeric}>{promoResult.message}</span>
                  </div>
                )}
              </div>

              {error && <Callout tone="danger" style={{ marginBottom: 12 }}>{error}</Callout>}

              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="white" icon="back" onClick={() => setStep(2)}>{t.prevBtn}</Btn>
                <Btn style={{ flex: 1 }} icon="check" onClick={handleSubmit} disabled={loading} loading={loading}>
                  {loading ? t.sending : t.submitBtn}
                </Btn>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Order;
